import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Calendar, Trash2, Clock, Plus, User, Target, Search, ChevronLeft, ChevronRight, Archive, Info, AlertTriangle } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { StudentData } from './StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { error as logError } from '../../../utils/logger';
import { getAssessmentHistory, DOMAIN_META, type AssessmentHistoryEntry, type TeacherAssignment } from '../../../assessment';
import { TeacherV2Route } from '../TeacherSidebarV2';
import { DjayNarrator } from '../../../mascot';
import { CreateAssessmentPanel } from '../components/CreateAssessmentPanel';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { InsightBar } from '../components/InsightBar';
import { EmptyState } from '../../../components/shared/EmptyState';
import { DataFreshness } from '../../shared/DataFreshness';
import { ClassroomSwitcher } from '../components/ClassroomSwitcher';
import { dataLayer } from '../../../data/dataLayer';
import { formatRelativeTime } from '../../../utils/formatters';
import { downloadCsv } from '../../../utils/csvExport';
import type { Account, Classroom } from '../../../accounts';
import { containerVariants, springItemVariants as itemVariants, tableRowVariants } from '../shared/animationVariants';
interface AssessmentsPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onNavigate?: (route: TeacherV2Route) => void;
  activeAccount?: Account;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  onSelectClassroom?: (id: string | null) => void;
  initialTab?: 'klaarzetten' | 'resultaten';
  initialSearchQuery?: string;
}
type MainTabType = 'klaarzetten' | 'resultaten';
const ITEMS_PER_PAGE = 20;
export function AssessmentsPage({
  students,
  selectedGroup,
  activeAccount,
  classrooms = [],
  selectedClassroomId,
  onSelectClassroom,
  initialTab,
  initialSearchQuery
}: AssessmentsPageProps) {
  const [assessmentHistory, setAssessmentHistory] = useState<AssessmentHistoryEntry[]>([]);
  const [assignments, setAssignments] = useState<TeacherAssignment[]>([]);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [mainTab, setMainTab] = useState<MainTabType>(initialTab || 'klaarzetten');
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastLoadedAt, setLastLoadedAt] = useState(() => new Date());
  /** AS-2026-07-P3-02: distinguish a failed load from "no data yet". */
  const [loadError, setLoadError] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    action: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    action: async () => {}
  });
  // Apply initial values when they change
  useEffect(() => {
    if (initialTab) setMainTab(initialTab);
  }, [initialTab]);
  useEffect(() => {
    if (initialSearchQuery !== undefined) setSearchQuery(initialSearchQuery);
  }, [initialSearchQuery]);
  const loadData = useCallback(async () => {
    try {
      const [history, assignmentsData] = await Promise.all([getAssessmentHistory(selectedGroup), dataLayer.getTeacherAssignments()]);
      setAssessmentHistory(history);
      // Filter assignments for this classroom/group
      const allAssignments = Object.values(assignmentsData);
      const relevantAssignments = allAssignments.filter(a => (a.classroomId || null) === (selectedClassroomId || null) || !a.classroomId && a.group === selectedGroup);
      setAssignments(relevantAssignments.sort((a, b) => b.createdAt - a.createdAt));
      setLastLoadedAt(new Date());
      setLoadError(false);
    } catch (err) {
      logError('[AssessmentsPage] loadData failed:', err);
      setAssessmentHistory([]);
      setAssignments([]);
      setLoadError(true);
    }
  }, [selectedGroup, selectedClassroomId]);
  useEffect(() => {
    loadData();
  }, [loadData]);
  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedDomain]);
  const filteredHistory = useMemo(() => {
    let filtered = assessmentHistory;
    // 1. Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(entry => {
        const student = students.find(s => s.id === entry.learnerId);
        const studentName = student ? student.name.toLowerCase() : 'onbekend';
        return studentName.includes(query);
      });
    }
    // 2. Domain / Type filter
    if (selectedDomain === 'niveaubepaling') {
      filtered = filtered.filter(entry => !entry.assignmentId);
    } else if (selectedDomain === 'toegewezen') {
      filtered = filtered.filter(entry => !!entry.assignmentId);
    } else if (selectedDomain !== 'all') {
      filtered = filtered.filter(entry => entry.domainId === selectedDomain);
    }
    return filtered;
  }, [assessmentHistory, searchQuery, selectedDomain, students]);
  const totalPages = Math.ceil(filteredHistory.length / ITEMS_PER_PAGE);
  const paginatedHistory = filteredHistory.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const avgAssessmentScore = assessmentHistory.length > 0 ? Math.round(assessmentHistory.reduce((sum, a) => sum + a.score, 0) / assessmentHistory.length) : 0;
  const uniqueLearnersTested = useMemo(() => {
    const uniqueIds = new Set(assessmentHistory.map(a => a.learnerId));
    return uniqueIds.size;
  }, [assessmentHistory]);
  const handleExportAssessmentCsv = () => {
    const headers = ['Datum', 'Leerling', 'Type', 'Domein', 'Score (%)', 'Aantal Vragen', 'Tijd (min)'];
    const rows = filteredHistory.map(a => {
      const dateStr = new Date(a.completedAt).toLocaleDateString('nl-NL');
      const student = students.find(s => s.id === a.learnerId);
      const studentName = student ? student.name : 'Onbekend';
      const typeLabel = a.assignmentId ? 'Toegewezen toets' : 'Niveaubepaling';
      const domainLabel = a.domainId ? DOMAIN_META[a.domainId]?.label : 'Onbekend';
      return [dateStr, `"${studentName}"`, `"${typeLabel}"`, `"${domainLabel}"`, a.score, a.totalQuestions, Math.round(a.totalTimeSeconds / 60)].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    downloadCsv(csvContent, 'toetsresultaten_export');
  };
  const handleDeleteAssignment = async (id: string) => {
    // Zie HomeworkPage: een kale "weet je het zeker?" laat de leerkracht
    // raden wat er verdwijnt. Bij een toets weegt dat extra, want hier hangt
    // een oordeel over een leerling aan — resultaten die een ouder of de
    // volgende leerkracht misschien nog nodig heeft.
    const assignment = assignments.find(a => a.id === id);
    const targetIds = assignment?.wholeClass ? students.map(s => s.id) : assignment?.targetStudentIds ?? [];
    const targetIdSet = new Set(targetIds);
    const target = targetIdSet.size;
    const done = (assignment?.completedByStudentIds || []).filter(sid => targetIdSet.has(sid)).length;
    setConfirmAction({
      isOpen: true,
      title: 'Toets verwijderen',
      message: assignment ? [`"${assignment.title}" wordt definitief verwijderd.`, '', `• Klaargezet voor ${target} ${target === 1 ? 'leerling' : 'leerlingen'}`,
      // De oude tekst zei dat de resultaten "mee verdwijnen". Dat doet
      // `deleteTeacherAssignment` niet: die haalt alleen de opdracht weg.
      // De uitslagen zelf staan in een ander domein (attempts/reports) en
      // blijven staan — maar wel losgekoppeld van de opdracht, dus voor
      // deze leerkracht onvindbaar. Een verwijdering beloven die niet
      // gebeurt, is hier het gevaarlijkst denkbare: iemand kan het
      // aanzien voor het afhandelen van een verwijderverzoek.
      done > 0 ? `• ${done} ${done === 1 ? 'leerling heeft' : 'leerlingen hebben'} deze toets al gemaakt — hun uitslag blijft bewaard, maar raakt los van deze toets en is hier niet meer terug te vinden` : '• Nog niemand heeft de toets gemaakt', '', 'Je kunt dit niet ongedaan maken. Wil je de resultaten in beeld houden, kies dan Archiveren.'].join('\n') : 'Deze toets wordt definitief verwijderd. Je kunt dit niet ongedaan maken.',
      action: async () => {
        await dataLayer.deleteTeacherAssignment(id);
        loadData();
      }
    });
  };
  const handleArchiveAssignment = async (id: string) => {
    setConfirmAction({
      isOpen: true,
      title: 'Toets archiveren',
      message: 'Weet je zeker dat je deze toets wilt archiveren?',
      action: async () => {
        await dataLayer.archiveTeacherAssignment(id);
        loadData();
      }
    });
  };
  const domainOptions = [{
    value: 'all',
    label: 'Alle Toetsen'
  }, {
    value: 'niveaubepaling',
    label: 'Alleen Niveaubepalingen'
  }, {
    value: 'toegewezen',
    label: 'Alleen Toegewezen Toetsen'
  }, ...Object.entries(DOMAIN_META).map(([id, meta]) => ({
    value: id,
    label: `Domein: ${meta.label}`
  }))];
  return <PageLayout title="Toetsen" description="Toetsresultaten en niveaubepaling" data-id="element-10010" data-mp="2ta6o3bnjz3u4">
      <motion.div className="max-w-5xl mx-auto space-y-6 pb-12" variants={containerVariants} initial="hidden" animate="visible" data-id="element-10011" data-mp="2tc9nuneicbc1">
        
        <div className="flex flex-wrap items-center justify-between gap-3 mb-[-12px]" data-id="element-10012" data-mp="rjbsokx9d6nx">
          {classrooms.length > 0 && onSelectClassroom ? <ClassroomSwitcher classrooms={classrooms} selectedClassroomId={selectedClassroomId || null} onSelectClassroom={onSelectClassroom} activeTeacherId={activeAccount?.id} data-id="element-10013" data-mp="mks38ajghcqt" /> : <span data-id="element-10014" data-mp="1j9ptfupthd2u" />}
          <DataFreshness updatedAt={lastLoadedAt} onRefresh={loadData} data-id="element-10015" data-mp="1ja7x3cet6tks" />
        </div>

        {/* AS-2026-07-P3-02: a failed load must not read as "no toetsen yet". */}
        {loadError && <motion.div variants={itemVariants} role="alert" data-id="element-10016" data-mp="1t9yupmyo1op3">
            <Card className="p-4 border-amber-200 bg-amber-50 flex items-start gap-3" data-id="element-10017" data-mp="15jcntz0eeago">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-10018" data-mp="15h08zupxsg7u" />
              <div className="flex-1 min-w-0" data-id="element-10019" data-mp="10i5f9xqfl78y">
                <p className="text-sm font-bold text-amber-900" data-id="element-10020" data-mp="1f9e8shxgfcrd">
                  Toetsgegevens konden niet geladen worden
                </p>
                <p className="text-xs text-amber-800 mt-0.5" data-id="element-10021" data-mp="1agcrk39rz14m">
                  Deze lijst is mogelijk leeg of incompleet. Er zijn geen
                  gegevens verwijderd — probeer te verversen.
                </p>
              </div>
              <button onClick={loadData} className="text-xs font-bold text-amber-900 underline hover:no-underline flex-shrink-0" data-id="element-10022" data-mp="2thdlpu2xiw9r">
              
                Verversen
              </button>
            </Card>
          </motion.div>}

        {/* Page-level Tab Switcher */}
        <motion.div variants={itemVariants} className="flex justify-center sm:justify-start" data-id="element-10023" data-mp="2jh38wakt3qv4">
          
          <div className="bg-slate-100 p-1 rounded-xl inline-flex relative" data-id="element-10024" data-mp="3c7lna7qzdenu">
            <button onClick={() => setMainTab('klaarzetten')} className={`relative px-6 py-2.5 rounded-lg text-sm font-bold transition-colors z-10 ${mainTab === 'klaarzetten' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-10025" data-mp="3c8eo6s39d9y2">
              
              {mainTab === 'klaarzetten' && <motion.div layoutId="activeTabIndicator" className="absolute inset-0 bg-white rounded-lg shadow-sm" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-10026" data-mp="376xnawkrxnhh" />}
              <span className="relative z-10" data-id="element-10027" data-mp="2aczsksahpfs">Toetsen</span>
            </button>
            <button onClick={() => setMainTab('resultaten')} className={`relative px-6 py-2.5 rounded-lg text-sm font-bold transition-colors z-10 ${mainTab === 'resultaten' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-10028" data-mp="2ch1qx6moedf">
              
              {mainTab === 'resultaten' && <motion.div layoutId="activeTabIndicator" className="absolute inset-0 bg-white rounded-lg shadow-sm" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-10029" data-mp="3jqumj85rvwz6" />}
              <span className="relative z-10" data-id="element-10030" data-mp="35qtkhpdruji4">Resultaten</span>
            </button>
          </div>
        </motion.div>

        {mainTab === 'klaarzetten' && <motion.div key="klaarzetten-tab" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-6" data-id="element-10031" data-mp="30u3410y21lw6">
          
            {/* Toets klaarzetten CTA */}
            <Card className="p-5 border-l-4 border-l-orange-400 border-slate-200 shadow-sm bg-white flex flex-col sm:flex-row items-start gap-4" data-id="element-10032" data-mp="17h4f7h32oc55">
              <DjayNarrator size="md" emotion="pointing" data-id="element-10033" data-mp="1ga7pjgif88vk" />
              <div className="flex-1" data-id="element-10034" data-mp="1gara74zghbda">
                <h2 className="text-lg font-black text-slate-900 mb-1" data-id="element-10035" data-mp="1l9avr4a50l54">
                  Toets klaarzetten
                </h2>
                <p className="text-sm text-slate-600 mb-3 max-w-xl leading-relaxed" data-id="element-10036" data-mp="ojtlsd44gcxm">
                  Zet een toets klaar voor je klas of specifieke leerlingen.
                </p>
                <Button onClick={() => setIsCreatePanelOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm" size="sm" data-id="element-10037" data-mp="xj6v95s8x1ic">
                
                  <Plus className="w-4 h-4 mr-2" data-id="element-10038" data-mp="12gq9w6suvgat" />
                  Nieuwe toets klaarzetten
                </Button>
              </div>
            </Card>

            {/* Active Assignments Section */}
            {assignments.length > 0 ? <div className="space-y-4" data-id="element-10039" data-mp="3mun1e90112sm">
                <h2 className="text-xl font-black text-slate-900" data-id="element-10040" data-mp="3mvpvsqwk5nda">
                  Klaargezette Toetsen
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-10041" data-mp="3hxwqe032wxe2">
                  {assignments.filter(a => a.status !== 'archived').map(assignment => {
              const meta = DOMAIN_META[assignment.domainId as keyof typeof DOMAIN_META];

              /*
               AS-2026-07-P1-04 (July 2026): progress is read from the
               assignment LEDGER, intersected with the learners this
               assignment actually targets.
                The previous implementation counted any attempt in the
               same domain finished after `createdAt`. That credited
               non-targeted classmates, credited self-initiated
               niveaubepalingen in the same domain, double-counted a
               learner who tested twice, and then hid the overcount
               behind Math.min() — which could silently flip the card
               to "voltooid" while nobody targeted had finished.
                `completedByStudentIds` is the authoritative signal
               (see TeacherAssignment docs); it is written only on a
               durably-persisted completion.
               */

              const targetIds = assignment.wholeClass ? students.map(s => s.id) : assignment.targetStudentIds;
              const targetIdSet = new Set(targetIds);
              const targetCount = targetIdSet.size;
              const completedCount = (assignment.completedByStudentIds || []).filter(sid => targetIdSet.has(sid)).length;
              const progressPercent = targetCount > 0 ? Math.round(completedCount / targetCount * 100) : 0;
              let status = assignment.status;
              if (assignment.deadline && Date.now() > assignment.deadline) status = 'expired';
              if (completedCount >= targetCount && targetCount > 0) status = 'completed';
              const Icon = meta?.icon;
              return <Card key={assignment.id} className="p-4 border-slate-200 flex flex-col bg-white shadow-sm hover:shadow-md transition-shadow" data-id="element-10042" data-mp="2c0rjtzvocb80">
                    
                          <div className="flex items-start justify-between mb-3" data-id="element-10043" data-mp="22af4cm2pv88q">
                            <div className="flex items-center gap-3" data-id="element-10044" data-mp="228c2n4s7bqo6">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta?.bgColor || 'bg-slate-100'} ${meta?.color || 'text-slate-500'}`} data-id="element-10045" data-mp="1xd6l0i5u0y5w">
                          
                                {Icon ? <Icon className="w-4 h-4" data-id="element-10046" data-mp="2q12c97cmtxva" /> : '📝'}
                              </div>
                              <div data-id="element-10047" data-mp="2kzd4c0kpi0bf">
                                <div className="flex items-center gap-2" data-id="element-10048" data-mp="2gzuq1nne4nhk">
                                  <h3 className="font-bold text-sm text-slate-900" data-id="element-10049" data-mp="2h1xfa5gpycl8">
                                    {assignment.domainLabel}
                                  </h3>
                                  {assignment.type === 'general' && <Badge variant="default" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] py-0 px-1.5" data-id="element-10050" data-mp="3h817kr6e92ln">
                              
                                      Algemeen
                                    </Badge>}
                                  {assignment.type === 'targeted' && <Badge variant="default" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] py-0 px-1.5" data-id="element-10051" data-mp="3uz63bi6n5aqp">
                              
                                      Gericht
                                    </Badge>}
                                </div>
                                {assignment.type === 'targeted' && assignment.selectedSkillSummary && <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-1" data-id="element-10052" data-mp="crgkth73rp05">
                                      {assignment.selectedSkillSummary}
                                    </div>}
                                <div className="text-[10px] text-slate-500 flex items-center gap-1.5 mt-1" data-id="element-10053" data-mp="2enaillb5a6j1">
                                  <Calendar className="w-3 h-3" data-id="element-10054" data-mp="pc5eh09ypq6j" />
                                  {new Date(assignment.createdAt).toLocaleDateString('nl-NL')}
                                  {assignment.deadline && <>
                                      <span data-id="element-10055" data-mp="z286oy99f03t">•</span>
                                      <span className="text-orange-600" data-id="element-10056" data-mp="z42r8bgmcq7d">
                                        Deadline:{' '}
                                        {new Date(assignment.deadline).toLocaleDateString('nl-NL')}
                                      </span>
                                    </>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1" data-id="element-10057" data-mp="bcdumiwrlfkm">
                              <button onClick={() => handleArchiveAssignment(assignment.id)} className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-md transition-colors" aria-label="Archiveren" title="Archiveren" data-id="element-10058" data-mp="bb2qghzeq5sz">
                          
                                <Archive className="w-4 h-4" data-id="element-10059" data-mp="34owcv3jk9nea" />
                              </button>
                              <button onClick={() => handleDeleteAssignment(assignment.id)} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-md transition-colors" aria-label="Verwijderen" data-id="element-10060" data-mp="39pbrdyfkx0xu">
                          
                                <Trash2 className="w-4 h-4" data-id="element-10061" data-mp="30poqreuobz5u" />
                              </button>
                            </div>
                          </div>

                          <div className="mt-auto space-y-2" data-id="element-10062" data-mp="3sdgr8ut481rd">
                            <div className="flex items-center justify-between text-xs" data-id="element-10063" data-mp="3enll04lnnthc">
                              <div className="flex items-center gap-2 flex-wrap" data-id="element-10064" data-mp="3jmgfi3szj6ha">
                                <span className="text-slate-600 font-medium" data-id="element-10065" data-mp="3jk3pd3d2ppt5">
                                  {completedCount} van {targetCount} voltooid
                                </span>
                                {/*
                                 F-AS-R1-ASSIGNED-ASSESSMENT-EXIT: surface
                                 students who started and abandoned this
                                 assignment so the teacher can follow up.
                                 Hidden when zero — keeps the card calm.
                                 Filters out students who later completed.
                                 */}
                                {(() => {
                        const abandoned = assignment.abandonedByStudentIds || [];
                        const completedSet = new Set(assignment.completedByStudentIds || []);
                        const stillAbandoned = abandoned.filter(sid => !completedSet.has(sid));
                        if (stillAbandoned.length === 0) return null;
                        return <span className="inline-flex items-center gap-1 text-amber-700 font-medium" title={`${stillAbandoned.length} leerling${stillAbandoned.length === 1 ? '' : 'en'} is gestopt voor het einde — mogelijk een gesprek waard.`} data-id="element-10066" data-mp="1ytz19ji92xx2">
                                
                                      <AlertTriangle className="w-3 h-3" data-id="element-10067" data-mp="3jbqdsrjjpm01" />
                                      {stillAbandoned.length} gestopt
                                    </span>;
                      })()}
                              </div>
                              {status === 'active' && <Badge variant="default" className="bg-indigo-100 text-indigo-700 text-[10px] py-0 px-1.5" data-id="element-10068" data-mp="3sb3p7pr8ckne">
                          
                                  Loopt nu
                                </Badge>}
                              {status === 'completed' && <Badge variant="success" className="bg-emerald-100 text-emerald-700 text-[10px] py-0 px-1.5" data-id="element-10069" data-mp="jwv2uzfawq3o">
                          
                                  Afgerond
                                </Badge>}
                              {status === 'expired' && <Badge variant="danger" className="bg-rose-100 text-rose-700 text-[10px] py-0 px-1.5" data-id="element-10070" data-mp="2lpsyf30gpwsb">
                          
                                  Deadline voorbij
                                </Badge>}
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-10071" data-mp="pld3zprqecs8">
                              <div className={`h-full rounded-full transition-all duration-1000 ${status === 'completed' ? 'bg-emerald-500' : status === 'expired' ? 'bg-rose-500' : 'bg-indigo-500'}`} style={{
                      width: `${progressPercent}%`
                    }} data-id="element-10072" data-mp="uid1vdefg989" />
                        
                            </div>
                          </div>
                        </Card>;
            })}
                </div>
              </div> : <motion.div className="mb-6" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.4
        }} data-id="element-10073" data-mp="1i9ipieqm05t4">
            
                <EmptyState title="Geen actieve toetsen" message="Je hebt op dit moment geen toetsen klaargezet voor je leerlingen." emotion="happy" colorScheme="slate" data-id="element-10074" data-mp="1rb8hbloel6rf" />
            
              </motion.div>}
          </motion.div>}

        {mainTab === 'resultaten' && <motion.div key="resultaten-tab" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-6" data-id="element-10075" data-mp="32rjk8o6pjwr8">
          
            {/* Insight Bar */}
            <InsightBar message={<>
                  <p data-id="element-10077" data-mp="3qhdwp4n47jay">
                    {assessmentHistory.length === 0 ? 'Er zijn nog geen toetsen afgenomen. Zet een toets klaar om te beginnen!' : `Er zijn ${assessmentHistory.length} toetsen afgenomen bij ${uniqueLearnersTested} leerlingen. De gemiddelde score is ${avgAssessmentScore}%.`}
                  </p>
                  <p className="text-xs text-indigo-500/70 mt-1.5 flex items-center gap-1" data-id="element-10078" data-mp="i1kbichu9jnk">
                    <Info className="w-3 h-3" data-id="element-10079" data-mp="dac41m02k8d1" />
                    Indicatief — geen officiële Cito-toets.
                  </p>
                </>} data-id="element-10076" data-mp="3uhxpxx4ki2rt" />
          

            {/* Toetsresultaten Section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-10080" data-mp="1ufu3ams89da">
              <div data-id="element-10081" data-mp="3k0khnkw3ga5g">
                <h2 className="text-xl font-black text-slate-900 mb-1" data-id="element-10082" data-mp="3k13wutud0kfs">
                  Toetsresultaten Historie
                </h2>
                <p className="text-sm text-slate-500" data-id="element-10083" data-mp="3p0qb96lxc6q6">
                  Overzicht van alle gemaakte toetsen in deze groep.
                </p>
              </div>
              <Button onClick={handleExportAssessmentCsv} variant="outline" disabled={filteredHistory.length === 0} size="sm" className="border-slate-200 text-slate-600 hover:bg-slate-50" data-id="element-10084" data-mp="kjdv8sgid7hk">
              
                <Download className="w-4 h-4 mr-2" data-id="element-10085" data-mp="bqspccxz6va5" />
                Exporteer CSV
              </Button>
            </div>

            {/* Filter Bar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6" data-id="element-10086" data-mp="148ejeefk9d7v">
              <div className="w-full sm:w-1/2 md:w-1/3" data-id="element-10087" data-mp="1dn82bw0c1l86">
                <Input placeholder="Zoek leerling..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-10089" data-mp="19js7n57tpuy4" />} size="sm" data-id="element-10088" data-mp="1dlnd8lwseuhp" />
              
              </div>
              <div className="w-full sm:w-1/2 md:w-1/3" data-id="element-10090" data-mp="1nfdup6uhy4nw">
                <Select options={domainOptions} value={selectedDomain} onChange={e => setSelectedDomain(e.target.value)} size="sm" data-id="element-10091" data-mp="1if7yreh71w00" />
              
              </div>
            </div>

            {paginatedHistory.length > 0 ? <>
                <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" variants={containerVariants} initial="hidden" animate="visible" data-id="element-10092" data-mp="qotum9yzfy1j">
              
                  {paginatedHistory.map((entry, idx) => {
              const dateStr = new Date(entry.completedAt).toLocaleDateString('nl-NL', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              });
              const relativeDate = formatRelativeTime(entry.completedAt);
              const isHigh = entry.score >= 70;
              const isMedium = entry.score >= 50 && entry.score < 70;
              const domainMeta = entry.domainId ? DOMAIN_META[entry.domainId] : null;
              const student = students.find(s => s.id === entry.learnerId);
              const studentName = student ? student.name : 'Onbekend';
              return <motion.div key={entry.attemptId} variants={tableRowVariants} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col" data-id="element-10093" data-mp="1zgc50pmaw70g" data-mpi={idx}>
                    
                        <div className="flex justify-between items-start mb-3" data-id="element-10094" data-mp="29cyr1hqd5442">
                          <div className="flex items-center gap-3" data-id="element-10095" data-mp="2e4qli3cn3tzh">
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0 border border-slate-200" data-id="element-10096" data-mp="1gp2ymp7ttyd3">
                              <User className="w-5 h-5" data-id="element-10097" data-mp="1lmbcidlxzg8j" />
                            </div>
                            <div data-id="element-10098" data-mp="1e3vgi46wh5al">
                              <div className="font-bold text-slate-900" data-id="element-10099" data-mp="195k4bmt4uip7">
                                {studentName}
                              </div>
                              <div className="text-xs text-slate-500 flex items-center gap-1" data-id="element-10100" data-mp="144w3k9f3qnhs">
                                <Clock className="w-3 h-3" data-id="element-10101" data-mp="z6umfu18ky8y" />
                                {relativeDate}{' '}
                                <span className="text-slate-300" data-id="element-10102" data-mp="v73pt9p13nwi">•</span>{' '}
                                {dateStr}
                              </div>
                            </div>
                          </div>
                          <div className="text-right" data-id="element-10103" data-mp="ldmsvwtlhsom">
                            <div className={`text-2xl font-black ${isHigh ? 'text-emerald-600' : isMedium ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-10104" data-mp="gi7ijeusqewy">
                          
                              {entry.score}%
                            </div>
                          </div>
                        </div>

                        <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between" data-id="element-10105" data-mp="1vdfjdi4r2ely">
                          <div className="flex items-center gap-2" data-id="element-10106" data-mp="209wit8hegfec">
                            {domainMeta ? <div className={`flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-md ${domainMeta.bgColor} ${domainMeta.color}`} data-id="element-10107" data-mp="1le6rqvxcxc2r">
                          
                                <domainMeta.icon className="w-3.5 h-3.5" data-id="element-10108" data-mp="1clmr5pxi8xfa" />
                                {domainMeta.label}
                              </div> : <div className="flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-md bg-slate-100 text-slate-600" data-id="element-10109" data-mp="1hf7snryb49j8">
                                <Target className="w-3.5 h-3.5" data-id="element-10110" data-mp="17miir1ien3q0" />
                                Algemeen
                              </div>}

                            {entry.assignmentId ? <span className="text-[10px] font-bold text-orange-600 uppercase tracking-wider" data-id="element-10111" data-mp="tpwoe0hqzx0c">
                                Toegewezen
                              </span> : <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider" data-id="element-10112" data-mp="f1dfnhll7g5p">
                                Niveaubepaling
                              </span>}
                          </div>

                          <div className="text-xs text-slate-500 font-medium" data-id="element-10113" data-mp="nzfbzjudlkmn">
                            {entry.totalQuestions} vr{' '}
                            <span className="text-slate-300 mx-1" data-id="element-10114" data-mp="xz64jfzdlpk3">•</span>{' '}
                            {Math.round(entry.totalTimeSeconds / 60)} min
                          </div>
                        </div>
                      </motion.div>;
            })}
                </motion.div>

                {/* Pagination Controls */}
                {totalPages > 1 && <div className="flex items-center justify-between border-t border-slate-100 pt-4 mb-6" data-id="element-10115" data-mp="12r7ntluyogjm">
                    <div className="text-sm text-slate-500" data-id="element-10116" data-mp="1go33vy9mr896">
                      Pagina{' '}
                      <span className="font-bold text-slate-900" data-id="element-10117" data-mp="1bozww8a3aryk">
                        {currentPage}
                      </span>{' '}
                      van{' '}
                      <span className="font-bold text-slate-900" data-id="element-10118" data-mp="107x0qr9aqc0r">
                        {totalPages}
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-10119" data-mp="q8ob2algrjk7">
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2" data-id="element-10120" data-mp="q868yxc4eb4d">
                  
                        <ChevronLeft className="w-4 h-4" data-id="element-10121" data-mp="ghtfgfmbv7m3" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2" data-id="element-10122" data-mp="ci2ni8l2elfz">
                  
                        <ChevronRight className="w-4 h-4" data-id="element-10123" data-mp="2hsez19z6275" />
                      </Button>
                    </div>
                  </div>}
              </> : <motion.div className="mb-6" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.4
        }} data-id="element-10124" data-mp="3trh9th9l23ub">
            
                <EmptyState title="Geen resultaten gevonden" message="Er zijn geen toetsen die voldoen aan je huidige filters." emotion="happy" colorScheme="slate" data-id="element-10125" data-mp="3tiujd7ux8app" />
            
              </motion.div>}
          </motion.div>}
      </motion.div>

      <CreateAssessmentPanel open={isCreatePanelOpen} onClose={() => setIsCreatePanelOpen(false)} students={students} selectedGroup={selectedGroup} teacherId={activeAccount?.id || ''} classroomId={selectedClassroomId || null} onSuccess={loadData} data-id="element-10126" data-mp="30wjidw3ek7sd" />
      

      <ConfirmDialog open={confirmAction.isOpen} title={confirmAction.title} message={confirmAction.message} onConfirm={async () => {
      await confirmAction.action();
      setConfirmAction(prev => ({
        ...prev,
        isOpen: false
      }));
    }} onCancel={() => setConfirmAction(prev => ({
      ...prev,
      isOpen: false
    }))} data-id="element-10127" data-mp="3fjurzsg0xhpu" />
      
    </PageLayout>;
}